import type { Nav, } from "@bracket/types";
import { Div, Grid, Flow, Text as Tx, Button, Loader, Tooltip, Typography, } from "@bracket/ui";
import { TextInput, } from "@components";
import { useSharedDraftable, } from "@bracket/platform/hooks";
import { FeesRollupCard, } from "@sections/StrategiesPage/components";
import { useVaults, } from "@store";
import styled from "styled-components";
import { getPercChange, } from "./helpers";



export const NavAndFeesCard: React.FC = () => {

	const {
		ready,
		activeAdminVault: vault,
		updateNav,
	} = useVaults();

	const {
		draft,
		setDraftKey,
		getChangedPartial,
	} = useSharedDraftable<Nav>(
		(ready && vault ? vault.nav : false) as Nav,
		{ instanceId: ready && vault ? vault.symbol : "NONE", emptyValues: true }
	);

	if(!ready || !vault || !draft)
		return <Loader />;

	const { nav, denomination: { label, }, } = vault;



	return (
		<Container>
			<Typography variant="paragraphXL" color="var(--text-heading-title)">
				Official NAV and Fees
			</Typography>

			<ContentContainer>

				<div style={{ gridColumn: 2 }}>
					<TitleContainer>
							<Tx size="lg" color="paragraph" justify="right">
								Current Value
							</Tx>
						<Tooltip message="The last Official NAV value certified for the vault" />
					</TitleContainer>
				</div>

				<div style={{ gridColumn: 3 }}>
					<TitleContainer>
						<Typography variant="paragraphLG" color="var(--text-paragraph)">
							Percentage Change
						</Typography>
						<Tooltip message="Percentage change from the last Official NAV value to the current NAV value" />
					</TitleContainer>
				</div>

				<InputContainer>
					<TextInput
						label="Official NAV"
						type="number"
						onChange={value => setDraftKey("onav", value)}
						placeholder={`${label} value per share in WEI`}
						value={draft.onav}
						regex={/^[0-9]*$/}
					/>
				</InputContainer>

				<ValueContainer>
					<Typography variant="paragraphLG" color="var(--text-paragraph)" textAlign="right">
						{nav.onav}
					</Typography>
				</ValueContainer>


				<ValueContainer>
					<Typography variant="paragraphLG" color="var(--text-paragraph)" textAlign="right">
						{ getPercChange("onav", draft, nav) }
					</Typography>
				</ValueContainer>


				<InputContainer>
					<TextInput
						label="Manager Performance Fee"
						type="number"
						onChange={value => setDraftKey("manager_performance_fee", value)}
						placeholder={`${label} value per share in WEI`}
						value={draft.manager_performance_fee.toString()}
						regex={/^[0-9]*$/}
					/>
				</InputContainer>
				<ValueContainer>
					<Typography variant="paragraphLG" color="var(--text-paragraph)" textAlign="right">
						{nav.manager_performance_fee.toString()}
					</Typography>
				</ValueContainer>

				<ValueContainer>
					<Typography variant="paragraphLG" color="var(--text-paragraph)" textAlign="right">
						{ getPercChange("manager_performance_fee", draft, nav) }
					</Typography>
				</ValueContainer>


				<TextInput
					label="Manager Fixed Fee"
					type="number"
					onChange={value => setDraftKey("manager_tvl_fee", value)}
					placeholder={`${label} value per share in WEI`}
					value={draft.manager_tvl_fee.toString()}
					regex={/^[0-9]*$/}
				/>

				<ValueContainer>
					<Typography variant="paragraphLG" color="var(--text-paragraph)" textAlign="right">
						{nav.manager_tvl_fee.toString()}
					</Typography>
				</ValueContainer>

				<ValueContainer>
					<Typography variant="paragraphLG" color="var(--text-paragraph)" textAlign="right">
						{ getPercChange("manager_tvl_fee", draft, nav) }
					</Typography>
				</ValueContainer>

				<TextInput
					label="Bracket Fixed Fee"
					type="number"
					onChange={value => setDraftKey("bracket_tvl_fee", value)}
					placeholder={`${label} value per share in WEI`}
					value={draft.bracket_tvl_fee.toString()}
					regex={/^[0-9]*$/}
				/>

				<ValueContainer>
					<Typography variant="paragraphLG" color="var(--text-paragraph)" textAlign="right">
						{nav.bracket_tvl_fee.toString()}
					</Typography>
				</ValueContainer>

				<ValueContainer>
					<Typography variant="paragraphLG" color="var(--text-paragraph)" textAlign="right">
						{ getPercChange("bracket_tvl_fee", draft, nav) }
					</Typography>
				</ValueContainer>

			</ContentContainer>
			<FeesRollupCard />
			<Button label="Update" variant="primary" disabled={Object.keys(getChangedPartial()).length === 0} onClick={() => updateNav(getChangedPartial())} />
		</Container>
	);

};


const Container = styled.div`
	display: grid;
	padding: 24px;
	gap: 32px;
	border-radius: 8px;
	background-color: var(--surface);
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ContentContainer = styled.div`
	display: grid;
	row-gap: 16px;
	column-gap: 24px;
	align-items: center;
	grid-template-columns: 1.1fr 1fr 0.85fr;
	grid-template-rows: auto;
`;

const TitleContainer = styled.div`
	display: flex;
	gap: 4px;
	align-items: center;
	justify-content: flex-end;
`;

const InputContainer = styled.div`
	min-width: 260px;
	@media (min-width: 768px) {
		min-width: none;
	}
`;

const ValueContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-top: 1.5rem;
`;
